import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import "../../css/superTreeview.css";
import { IconButton } from "@fluentui/react/lib/Button";
import {
  Stack,
  Sticky,
  StickyPositionType,
  ConstrainMode,
  ScrollablePane,
  ScrollbarVisibility,
  Label,
  Text,
  TooltipHost,
  DirectionalHint,
} from "@fluentui/react";

import { DATA_LOAD_STATUS } from "../../redux/constants/metaData";
import { useCheckin } from "../../redux/hooks/useCheckin";
import { useLogin } from "../../redux/hooks/useLogin";
import { useMetadata } from "../../redux/hooks/useMetadata";
import { useTools } from "../../redux/hooks/useTools";
import { FAILED_LOGICALUNIT_FOUND } from "../../redux/constants/messageTypes";

import {
  REFRESH_TOKEN_ACTION,
  RESPONSE_TYPE,
} from "../../redux/constants/metaData";
import {
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
} from "@fluentui/react/lib/DetailsList";

import { useTheme } from "../../theme/OAS365Theme";
import { useCheckinPanelStyle } from "../../theme/styles/CheckinPanelStyle";
import { useUploadFileRecords } from "../../theme/styles/UploadFileRecordsStyle";
import Loader from "../LoadSpinner";
import deleteIcon from "../../assets/deleteCircledOutline.svg";
import noContentIcon from "../../assets/svg/noSearchResults.svg";

const SelectedObjectConnection = ({ embedded = false }) => {
  const {
    selectedObjectConnections,
    advSearchSelectedObjs,
    recentObjectsSelected,
    onObjectConnectionsLoad,
  } = useCheckin();
  const { teamsContext } = useLogin();
  const { objectConnectionDataLoad } = useMetadata();
  const {
    onSelectedObjectConnectionChanged,
  } = useTools();
  const sbWidth = 10;
  const sbHeight = 10;
  const sbBg = "white";
  const sbThumbBg = "lightgray";
  const binIcon = { iconName: "Refresh" };
  const infoIcon = { iconName: "Info" };
  const dataLoadingStatus = {
    completed: "Completed",
    loading: "Loading",
    nodata: "NoData",
    error: "Error",
  };
  const styleTheme = useUploadFileRecords();
  const panelStyle = useCheckinPanelStyle();
  const theme = useTheme();
  const [screenWidth, setScreenWidth] = useState(
    isWideScreen(window.innerWidth),
  );
  const [dataLoad, setDataLoad] = useState(dataLoadingStatus.completed);
  const [removeItem, setRemoveItem] = useState({
    luName: "",
    keyRef: "",
    selectType: "",
  });
  const loaderBorderStyle = {
    padding: "10px autopx",
    bottom: "50px",
    marginTop: "20px",
  };
  const messageBorderStyle = {
    margin: "auto",
    width: "200px",
    paddingLeft: "0px",
  };

  useEffect(() => {
    const hasCachedItems =
      Array.isArray(selectedObjectConnections) &&
      selectedObjectConnections.length > 0;

    switch (objectConnectionDataLoad) {
      case DATA_LOAD_STATUS.onStart:
      case DATA_LOAD_STATUS.onLoad:
        setDataLoad(
          hasCachedItems
            ? dataLoadingStatus.completed
            : dataLoadingStatus.loading,
        );
        break;
      case DATA_LOAD_STATUS.noDate:
        setDataLoad(dataLoadingStatus.nodata);
        break;
      case DATA_LOAD_STATUS.error:
        setDataLoad(
          hasCachedItems
            ? dataLoadingStatus.completed
            : dataLoadingStatus.error,
        );
        break;
      case DATA_LOAD_STATUS.completed:
      case DATA_LOAD_STATUS.partialCompleted:
        setDataLoad(dataLoadingStatus.completed);
        break;
      default:
        setDataLoad(
          hasCachedItems
            ? dataLoadingStatus.completed
            : dataLoadingStatus.loading,
        );
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectConnectionDataLoad, selectedObjectConnections]);

  useLayoutEffect(() => {
    if (selectedObjectConnections) {
      updateItems(selectedObjectConnections, false);
    }
  }, [selectedObjectConnections]);

  useEffect(() => {
    function handleResize() {
      setScreenWidth(isWideScreen(window.innerWidth));
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function isWideScreen(width) {
    if (width > 1023) {
      return true;
    } else {
      return false;
    }
  }

  useEffect(() => {
    if (removeItem.luName !== "") {
      const recentObjects = Array.isArray(recentObjectsSelected)
        ? [...recentObjectsSelected]
        : [];
      const searchObjects = Array.isArray(advSearchSelectedObjs)
        ? [...advSearchSelectedObjs]
        : [];

      const removePredicate = (obj) =>
        obj.keyRef === removeItem.keyRef && obj.luName === removeItem.luName;

      // Inherited bulk-settings / predefined connections lack selectType but live in
      // recentObjectsSelected after hydration via onSelectedObjectConnectionChanged(connections, []).
      onSelectedObjectConnectionChanged(
        recentObjects.filter((obj) => !removePredicate(obj)),
        searchObjects.filter((obj) => !removePredicate(obj)),
      );
      setRemoveItem({ luName: "", keyRef: "", selectType: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeItem]);

  const updateItems = (newItems, columnClicked) => {
    setTableItems(newItems);
  };

  function onColumnClick(column) {
    setRemoveItem({
      luName: column.luName,
      keyRef: column.keyRef,
      selectType: column.selectType,
    });
  }

  const getKey = (item, index) => {
    return item.key;
  };

  const cellTextStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "inline-block",
    maxWidth: "100%",
    fontFamily: theme.fonts?.regular,
    fontSize: `${theme.fontSizes?.normal ?? 12}px`,
    color: theme.colors?.fontColor,
  };

  const columns = [
    {
      key: "NameDesc",
      fieldName: "NameDesc",
      name: "Object Type",
      minWidth: 150,
      maxWidth: 200,
      isRowHeader: true,
      isResizable: true,
      isSorted: false,
      isSortedDescending: false,
      sortAscendingAriaLabel: "Sorted A to Z",
      sortDescendingAriaLabel: "Sorted Z to A",
      onRender: (selectedItems) => (
        <TooltipHost
          content={selectedItems.nameDesc}
          directionalHint={DirectionalHint.bottomCenter}
        >
          <span style={cellTextStyle}>{selectedItems.nameDesc}</span>
        </TooltipHost>
      ),
      isPadded: true,
    },
    {
      key: "KeyDesc",
      fieldName: "KeyDesc",
      name: "Object Key",
      minWidth: 200,
      maxWidth: 200,
      isRowHeader: true,
      isResizable: true,
      isSorted: false,
      isSortedDescending: false,
      sortAscendingAriaLabel: "Sorted A to Z",
      sortDescendingAriaLabel: "Sorted Z to A",
      onRender: (selectedItems) => (
        <TooltipHost
          content={selectedItems.keyDesc}
          directionalHint={DirectionalHint.bottomCenter}
        >
          <span style={cellTextStyle}>
            {selectedItems.keyDesc}
          </span>
        </TooltipHost>
      ),
      isPadded: true,
    },
    {
      key: "DocObjDesc",
      fieldName: "DocObjDesc",
      name: "Description",
      minWidth: 200,
      maxWidth: 200,
      isRowHeader: true,
      isResizable: true,
      isSorted: false,
      isSortedDescending: false,
      sortAscendingAriaLabel: "Sorted A to Z",
      sortDescendingAriaLabel: "Sorted Z to A",
      onRender: (item) => (
        <TooltipHost
          content={item.docObjDesc}
          directionalHint={DirectionalHint.bottomCenter}
        >
          <span style={cellTextStyle}>{item.docObjDesc}</span>
        </TooltipHost>
      ),
      isPadded: true,
    },
    {
      key: "delete",
      width: "auto",
      onColumnClick: onColumnClick,
      onRender: (selectedItems) => {
        return (
          <div style={{ marginTop: "-7px" }}>
            <button
              style={styleTheme.StackItemRemoveButtonStyle}
              onClick={(ev) => {
                onColumnClick(selectedItems);
              }}
              title="Remove"
            >
              <img
                src={deleteIcon}
                alt="Delete"
                style={{
                  marginTop: 5,
                  width: "20px",
                }}
              />{" "}
            </button>
          </div>
        );
      },
    },
  ];

  const columnsMiniScreen = [
    {
      key: "NameDesc",
      fieldName: "NameDesc",
      name: "Object Type",
      minWidth: 150,
      maxWidth: 200,
      isRowHeader: true,
      isResizable: true,
      isSorted: false,
      isSortedDescending: false,
      sortAscendingAriaLabel: "Sorted A to Z",
      sortDescendingAriaLabel: "Sorted Z to A",

      onRender: (selectedItems) => (
        <TooltipHost
          content={selectedItems.nameDesc}
          directionalHint={DirectionalHint.bottomCenter}
        >
          <span style={cellTextStyle}>{selectedItems.nameDesc}</span>
        </TooltipHost>
      ),
      isPadded: true,
    },
    {
      key: "DocObjDesc",
      fieldName: "DocObjDesc",
      name: "Description",
      minWidth: 200,
      maxWidth: 200,
      isRowHeader: true,
      isResizable: true,
      isSorted: false,
      isSortedDescending: false,
      sortAscendingAriaLabel: "Sorted A to Z",
      sortDescendingAriaLabel: "Sorted Z to A",

      onRender: (item) => (
        <TooltipHost
          content={item.docObjDesc}
          directionalHint={DirectionalHint.bottomCenter}
        >
          <span style={cellTextStyle}>{item.docObjDesc}</span>
        </TooltipHost>
      ),
      isPadded: true,
    },
    {
      key: "ObjectKeyToolTip",
      minWidth: 30,
      maxWidth: 30,
      onColumnClick: onColumnClick,
      onRender: (selectedItems) => {
        return (
          <TooltipHost
            content={
              <div style={{ display: "flex", alignItems: "center" }}>
                <div style={{ flexDirection: "column" }}>
                  <p
                    style={{
                      margin: 0,
                      fontFamily:
                        theme.contextMenu?.fontFamily ??
                        theme.fonts?.regular ??
                        "Poppins, sans-serif",
                      fontSize: `${theme.contextMenu?.fontSize ?? theme.fontSizes?.normal ?? 12}px`,
                    }}
                  >
                    {selectedItems.keyDesc}
                  </p>
                </div>
              </div>
            }
            tooltipProps={{
              calloutProps: {
                styles: {
                  fontColor: theme.colors.addovationWhite,
                  beak: {
                    background: theme.colors.addovation70Green,
                    color: theme.colors.addovationWhite,
                  },
                  beakCurtain: {
                    background: theme.colors.addovation70Green,
                    color: theme.colors.addovationWhite,
                  },
                  calloutMain: {
                    background: theme.colors.addovation70Green,
                    color: theme.colors.addovationWhite,
                  },
                },
              },
              styles: {
                content: {
                  color: theme.colors.addovationWhite,
                  fontFamily:
                    theme.contextMenu?.fontFamily ??
                    theme.fonts?.regular ??
                    "Poppins, sans-serif",
                  fontSize: `${theme.contextMenu?.fontSize ?? theme.fontSizes?.normal ?? 12}px`,
                },
              },
            }}
          >
            <IconButton
              style={{
                marginTop: 2,
                padding: 5,
                height: "20px",
                width: "20px",
              }}
              iconProps={infoIcon}
            />
          </TooltipHost>
        );
      },
    },
    {
      key: "delete",
      minWidth: 30,
      maxWidth: 30,
      onColumnClick: onColumnClick,
      onRender: (selectedItems) => {
        return (
          <div>
            <button
              style={styleTheme.StackItemRemoveButtonStyle}
              onClick={(ev) => {
                onColumnClick(selectedItems);
              }}
              title="Remove"
            >
              <img
                src={deleteIcon}
                alt="Delete"
                style={{
                  width: "20px",
                }}
              />{" "}
            </button>
          </div>
        );
      },
    },
  ];

  const [tableColumns, setTableColumns] = useState(columns);
  const [tableColumnsMiniScreen, setTableColumnsMiniScreen] =
    useState(columnsMiniScreen);
  const [tableItems, setTableItems] = useState([]);

  const embeddedPanelItemStyles = { root: { width: "100%", minHeight: 200 } };

  switch (dataLoad) {
    case dataLoadingStatus.completed:
      if (
        selectedObjectConnections !== undefined &&
        selectedObjectConnections !== null &&
        selectedObjectConnections.length > 0
      ) {
        if (embedded) {
          const scrollSurfaceStyle = {
            width: "100%",
            paddingRight: 4,
            paddingBottom: 2,
          };
          const cardBg = "#E8F5E9";
          const cardRing = "rgba(76, 141, 106, 0.22)";
          /** Mint cards: always dark text (theme fontColor is light in dark mode). */
          const textColor = "#000000";

          return (
            <div style={{ marginTop: 0, width: "100%" }}>
              <div style={scrollSurfaceStyle}>
                {tableItems.map((item) => (
                  <div
                    key={item.key ?? `${item.luName}-${item.keyRef}`}
                    style={{
                      backgroundColor: cardBg,
                      
                      padding: "8px 14px",
                      marginBottom: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 14,
                      
                    }}
                  >
                    <div
                      style={{
                        minWidth: 0,
                        flex: 1,
                        textAlign: "left",
                        cursor: "default",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 12,
                          color: textColor,
                          fontFamily: theme.fonts?.regular,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.nameDesc ?? ""}
                      </div>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 11,
                          color: textColor,
                          fontFamily: theme.fonts?.regular,
                          marginTop: 4,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.docObjDesc ?? ""}
                      </div>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 11,
                          color: textColor,
                          fontFamily: theme.fonts?.regular,
                          marginTop: 4,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.keyDesc ?? ""}
                      </div>
                    </div>
                    <button
                      type="button"
                      title="Remove"
                      aria-label="Remove object connection"
                      style={{
                        flexShrink: 0,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        padding: 4,
                        lineHeight: 0,
                      }}
                      onClick={() => onColumnClick(item)}
                    >
                      <img
                        src={deleteIcon}
                        alt=""
                        style={{
                          width: 24,
                          height: 24,
                          display: "block",
                          objectFit: "contain",
                          transform: "scale(0.92)",
                          transformOrigin: "center",
                        }}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        }
        if (screenWidth === true) {
          return (
            <Stack
              style={{ marginTop: embedded ? 0 : "10px" }}
              verticalAlign="center"
              tokens={{ childrenGap: 5 }}
            >
              <Stack.Item
                grow
                styles={
                  embedded
                    ? embeddedPanelItemStyles
                    : panelStyle.ObjectConnectionPanelSytle
                }
              >
                <ScrollablePane
                  scrollbarVisibility={ScrollbarVisibility.auto}
                  styles={{
                    root: {
                      marginLeft: embedded ? 0 : 10,
                      marginRight: embedded ? 0 : 10,
                      marginTop: embedded ? 0 : 100,
                      width: embedded ? "100%" : undefined,
                      height: embedded ? 280 : undefined,
                      minHeight: embedded ? 200 : undefined,
                      maxHeight: embedded ? 280 : undefined,
                      selectors: {
                        ".ms-ScrollablePane--contentContainer": {
                          scrollbarWidth: sbWidth,
                          scrollbarColor: `${theme.colors.scrollbarThumb} ${theme.colors.scrollbarBackground}`,
                        },
                        ".ms-ScrollablePane--contentContainer::-webkit-scrollbar":
                          {
                            width: sbWidth,
                            height: sbHeight,
                          },
                        ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-track":
                          {
                            background: theme.colors.scrollbarBackground,
                          },
                        ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-thumb":
                          {
                            background: theme.colors.scrollbarThumb,
                          },
                      },
                    },
                  }}
                >
                  <DetailsList
                    styles={{
                      root: {
                        selectors: {
                          ".ms-DetailsRow-check": {
                            visibility: "hidden",
                          },
                          ".ms-DetailsRow:hover": {
                            backgroundColor: `${theme.colors.msTeamsFilesHover}!important`,
                            fontColor: `${theme.colors.fontColor}!important`,
                          },
                          ".ms-Button-flexContainer:hover": {
                            backgroundColor: `${theme.colors.msTeamsFilesHover}!important`,
                            color: `${theme.colors.fontColor}!important`,
                          },
                          ".ms-Button-flexContainer": {
                            marginLeft: "-10px",
                          },
                          ".ms-DetailsRow": {
                            backgroundColor: embedded
                              ? "#E8F5E9!important"
                              : `${theme.colors.mainAreaBackgroundColor}!important`,
                            fontColor: `${theme.colors.fontColor}`,
                            borderRadius: embedded ? "8px" : undefined,
                            marginBottom: embedded ? "8px" : undefined,
                          },
                          ".ms-Button": {
                            fontFamily: theme.fonts.regular,
                            fontSize: "12px",
                            marginTop: "0px",
                          },
                          ".ms-Button:disabled": {
                            backgroundColor: "transparent",
                          },
                          ".ms-Button:selected ": {
                            backgroundColor: `${theme.colors.themeColor}!important`,
                          },
                          ".ms-DetailsRow:selected:hover": {
                            backgroundColor: `${theme.colors.themeColor}!important`,
                          },
                          ".ms-DetailsHeader-cellName": {
                            fontFamily: theme.fonts.regular,
                            fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                            backgroundColor: `${theme.colors.mainAreaBackgroundColor}!important`,
                            color: `${theme.colors.fontColor}`,
                          },
                          ".ms-DetailsRow-cell": {
                            fontFamily: theme.fonts.regular,
                            fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                            color: `${theme.colors.fontColor}!important`,
                          },
                          ".ms-FocusZone": {
                            backgroundColor: `${theme.colors.mainAreaBackgroundColor}!important`,
                          },
                          ".ms-FocusZone:hover": {
                            backgroundColor: `${theme.colors.mainAreaBackgroundColor}!important`,
                          },
                          ".ms-DetailsHeader-cellSizer": {
                            backgroundColor: `${theme.colors.mainAreaBackgroundColor}!important`,
                          },
                          ".ms-DetailsHeader-cellTitle": {
                            backgroundColor: `${theme.colors.mainAreaBackgroundColor}!important`,
                          },
                          ".ms-DetailsHeader-cellTitle": {
                            backgroundColor: `${theme.colors.mainAreaBackgroundColor}!important`,
                          },
                          ".ms-DetailsHeader-cellTitle": {
                            backgroundColor: `${theme.colors.mainAreaBackgroundColor}!important`,
                          },
                          ".ms-DetailsHeader-cellTitle": {
                            backgroundColor: `${theme.colors.mainAreaBackgroundColor}!important`,
                          },
                          ".ms-DetailsHeader-cellTitle": {
                            backgroundColor: `${theme.colors.mainAreaBackgroundColor}!important`,
                          },
                          ".ms-DetailsHeader-cellTitle": {
                            backgroundColor: `${theme.colors.mainAreaBackgroundColor}!important`,
                          },
                          ".ms-DetailsHeader-cellTitle": {
                            backgroundColor: `${theme.colors.mainAreaBackgroundColor}!important`,
                          },
                          ".ms-DetailsHeader-cellTitle": {
                            backgroundColor: `${theme.colors.mainAreaBackgroundColor}!important`,
                          },
                          ".ms-DetailsHeader-cellTitle": {
                            backgroundColor: `${theme.colors.mainAreaBackgroundColor}!important`,
                          },
                          ".ms-DetailsHeader-cellTitle": {
                            backgroundColor: `${theme.colors.mainAreaBackgroundColor}!important`,
                          },
                          ".ms-DetailsHeader-cell.is-actionable.is-empty": {
                            backgroundColor: theme.colors.tableBackgroundColor,
                          },
                        },
                      },
                    }}
                    items={tableItems}
                    compact={false}
                    columns={tableColumns}
                    selectionMode={SelectionMode.none}
                    getKey={getKey}
                    layoutMode={DetailsListLayoutMode.fixedColumns}
                    constrainMode={ConstrainMode.unconstrained}
                    isHeaderVisible={!embedded}
                    selectionPreservedOnEmptyClick={true}
                    enterModalSelectionOnTouch={true}
                    ariaLabelForSelectionColumn="Toggle selection"
                    ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                    checkButtonAriaLabel="select row"
                    visibility="false"
                    onRenderDetailsHeader={
                      embedded
                        ? undefined
                        : (headerProps, defaultRender) => {
                            return (
                              <Sticky
                                stickyPosition={StickyPositionType.Header}
                                isScrollSynced={true}
                                stickyBackgroundColor="transparent"
                              >
                                {defaultRender({
                                  ...headerProps,
                                  styles: {
                                    root: {
                                      background: "#f5f5f5",
                                      borderBottom: "1px solid #ddd",
                                      paddingTop: 1,
                                    },
                                  },
                                })}
                              </Sticky>
                            );
                          }
                    }
                  />
                </ScrollablePane>
              </Stack.Item>
            </Stack>
          );
        } else {
          return (
            <Stack
              style={{ marginTop: embedded ? 0 : "10px" }}
              verticalAlign="center"
              tokens={{ childrenGap: 5 }}
            >
              <Stack.Item
                grow
                styles={
                  embedded
                    ? embeddedPanelItemStyles
                    : panelStyle.ObjectConnectionPanelSytle
                }
              >
                <ScrollablePane
                  scrollbarVisibility={ScrollbarVisibility.auto}
                  styles={{
                    root: {
                      marginLeft: embedded ? 0 : 10,
                      marginRight: embedded ? 0 : 10,
                      marginTop: embedded ? 0 : 100,
                      width: embedded ? "100%" : undefined,
                      height: embedded ? 280 : undefined,
                      minHeight: embedded ? 200 : undefined,
                      maxHeight: embedded ? 280 : undefined,
                      selectors: {
                        ".ms-ScrollablePane--contentContainer": {
                          scrollbarWidth: sbWidth,
                          scrollbarColor: `${theme.colors.scrollbarThumb} ${theme.colors.scrollbarBackground}`,
                        },
                        ".ms-ScrollablePane--contentContainer::-webkit-scrollbar":
                          {
                            width: sbWidth,
                            height: sbHeight,
                          },
                        ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-track":
                          {
                            background: theme.colors.scrollbarBackground,
                          },
                        ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-thumb":
                          {
                            background: theme.colors.scrollbarThumb,
                          },
                      },
                    },
                  }}
                >
                  <DetailsList
                    styles={{
                      root: {
                        selectors: {
                          ".ms-DetailsRow-check": {
                            visibility: "hidden",
                          },
                          ".ms-DetailsRow:hover": {
                            backgroundColor: `${theme.colors.msTeamsFilesHover}!important`,
                            fontColor: `${theme.colors.fontColor}!important`,
                          },
                          ".ms-Button-flexContainer:hover": {
                            backgroundColor: `${theme.colors.msTeamsFilesHover}!important`,
                            color: `${theme.colors.fontColor}!important`,
                          },
                          ".ms-Button-flexContainer": {
                            marginLeft: "-10px",
                          },
                          ".ms-DetailsRow": {
                            backgroundColor: embedded
                              ? "#E8F5E9!important"
                              : `${theme.colors.mainAreaBackgroundColor}!important`,
                            fontColor: `${theme.colors.fontColor}`,
                            borderRadius: embedded ? "8px" : undefined,
                            marginBottom: embedded ? "8px" : undefined,
                          },
                          ".ms-Button": {
                            fontFamily: theme.fonts.regular,
                            fontSize: "12px",
                            marginTop: "0px",
                          },
                          ".ms-Button:disabled": {
                            backgroundColor: "transparent",
                          },
                          ".ms-Button:selected ": {
                            backgroundColor: `${theme.colors.themeColor}!important`,
                          },
                          ".ms-DetailsRow:selected:hover": {
                            backgroundColor: `${theme.colors.themeColor}!important`,
                          },
                          ".ms-DetailsHeader-cellName": {
                            fontFamily: theme.fonts.regular,
                            backgroundColor: `${theme.colors.mainAreaBackgroundColor}!important`,
                            color: `${theme.colors.fontColor}`,
                          },
                          ".ms-DetailsRow-cell": {
                            fontFamily: theme.fonts.regular,
                            fontSize: "14px",
                            color: `${theme.colors.fontColor}!important`,
                          },
                          ".ms-FocusZone": {
                            backgroundColor: `${theme.colors.mainAreaBackgroundColor}!important`,
                          },
                          ".ms-FocusZone:hover": {
                            backgroundColor: `${theme.colors.mainAreaBackgroundColor}!important`,
                          },
                          ".ms-DetailsHeader-cellSizer": {
                            backgroundColor: `${theme.colors.mainAreaBackgroundColor}!important`,
                          },
                          ".ms-DetailsHeader-cellTitle": {
                            backgroundColor: `${theme.colors.mainAreaBackgroundColor}!important`,
                          },
                          ".ms-DetailsHeader-cell.is-actionable.is-empty": {
                            backgroundColor: theme.colors.tableBackgroundColor,
                          },
                        },
                      },
                    }}
                    items={tableItems}
                    compact={false}
                    columns={tableColumnsMiniScreen}
                    selectionMode={SelectionMode.none}
                    getKey={getKey}
                    layoutMode={DetailsListLayoutMode.fixedColumns}
                    constrainMode={ConstrainMode.unconstrained}
                    isHeaderVisible={!embedded}
                    selectionPreservedOnEmptyClick={true}
                    enterModalSelectionOnTouch={true}
                    ariaLabelForSelectionColumn="Toggle selection"
                    ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                    checkButtonAriaLabel="select row"
                    visibility="false"
                    onRenderDetailsHeader={
                      embedded
                        ? undefined
                        : (headerProps, defaultRender) => {
                            return (
                              <Sticky
                                stickyPosition={StickyPositionType.Header}
                                isScrollSynced={true}
                                stickyBackgroundColor="transparent"
                              >
                                {defaultRender({
                                  ...headerProps,
                                  styles: {
                                    root: {
                                      background: "#f5f5f5",
                                      borderBottom: "1px solid #ddd",
                                      paddingTop: 1,
                                    },
                                  },
                                })}
                              </Sticky>
                            );
                          }
                    }
                  />
                </ScrollablePane>
              </Stack.Item>
            </Stack>
          );
        }
      } else {
        if (embedded) {
          return (
            <div
              style={{
                padding: "20px 16px",
                textAlign: "center",
                width: "100%",
              }}
            >
              <Text
                variant="medium"
                 styles={{
            root: {
              color: "#4c8d6a",
              fontFamily: "Poppins, sans-serif",
              fontWeight: 600,
              display: "block",
              width: "100%",
              textAlign: "center",
              marginBottom: embedded ? 0 : 16,
              fontSize: `${theme.fontSizes?.normal ?? 12}px`,
            },
          }}
              >
                No objects selected. Choose from Favorites or Search.
              </Text>
            </div>
          );
        }
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              minHeight: 280,
            }}
          >
            <div style={{ textAlign: "center", width: 400 }}>
              <Text
                variant="medium"
                styles={{
                  root: {
                    color: "#4c8d6a",
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 600,
                    display: "block",
                    width: "100%",
                    textAlign: "center",
                    marginBottom: 16,
                    fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                  },
                }}
              >
                No objects selected. Choose from Favorites or Search.
              </Text>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <img
                  src={noContentIcon}
                  style={{
                    width: 240,
                    height: 240,
                    objectFit: "contain",
                  }}
                  alt="No object connections"
                />
              </div>
            </div>
          </div>
        );
      }
    case dataLoadingStatus.loading:
      return (
        <>
          <div style={loaderBorderStyle}>
            <div
              style={{
                margin: "auto",
                width: "50px",
                paddingLeft: "0px",
              }}
            >
              <Loader />
            </div>
          </div>
        </>
      );
    case dataLoadingStatus.nodata:
      return (
        <div
          style={{
            padding: embedded ? "12px 10px" : "20px",
            textAlign: "center",
            width: "100%",
          }}
        >
          <Label style={styleTheme.LabelInformationStyle}>
            {FAILED_LOGICALUNIT_FOUND}
          </Label>
          <div
            style={{
              marginTop: 8,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <IconButton
              iconProps={binIcon}
              title="Refresh"
              ariaLabel="Refresh"
              onClick={() => {
                onObjectConnectionsLoad(teamsContext, {
                  retry: 0,
                  actionType: REFRESH_TOKEN_ACTION.logicalUnitsLoad,
                  refreshResponse: RESPONSE_TYPE.none,
                });
              }}
            />
          </div>
        </div>
      );
    case dataLoadingStatus.error:
      return (
        <>
          <div style={loaderBorderStyle}>
            <div
              style={{
                margin: embedded ? "8px auto" : "auto",
                width: embedded ? "100%" : "200px",
                paddingLeft: "0px",
                textAlign: "center",
              }}
            >
              <IconButton
                style={embedded ? {} : { marginLeft: "80px" }}
                iconProps={binIcon}
                title="Refresh"
                ariaLabel="Refresh"
                onClick={(ev) => {
                  onObjectConnectionsLoad(teamsContext, {
                    retry: 0,
                    actionType: REFRESH_TOKEN_ACTION.logicalUnitsLoad,
                    refreshResponse: RESPONSE_TYPE.none,
                  });
                }}
              />
              <Label style={styleTheme.LabelErrorMessageStyle}>
                {FAILED_LOGICALUNIT_FOUND}
              </Label>
            </div>
          </div>
        </>
      );
    default:
      return null;
  }
};

export default SelectedObjectConnection;
